import { Link } from "react-router-dom";
import {
  ArrowLeftRight,
  Droplets,
  Coins,
  Zap,
  Shield,
  TrendingUp,
  ChevronRight,
  Sparkles,
  Lock,
  Rocket,
  Link2,
} from "lucide-react";
import { useLaunchpadConfig } from "../hooks/useLaunchpad";

const EMBLEM = "/jasmy-emblem.jpg";
const GENIE  = "/djnn-genie.png";
const HERO   = "/jasmy-hero.png";

function FeatureCard({
  title,
  description,
  icon: Icon,
  href,
  accent,
}: {
  title: string;
  description: string;
  icon: typeof Zap;
  href: string;
  accent: string;
}) {
  return (
    <Link to={href} className="glass-aqua group block p-6">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: `${accent}1f`, boxShadow: `0 0 24px ${accent}22` }}
      >
        <Icon className="w-6 h-6" style={{ color: accent }} />
      </div>
      <h3 className="font-display text-lg font-semibold text-text-primary mb-2">{title}</h3>
      <p className="text-sm text-text-secondary leading-relaxed mb-4">{description}</p>
      <span
        className="inline-flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all"
        style={{ color: accent }}
      >
        Launch <ChevronRight className="w-4 h-4" />
      </span>
    </Link>
  );
}

function StatPill({ label, value, icon: Icon }: { label: string; value: string; icon: typeof Zap }) {
  return (
    <div className="glass-aqua px-5 py-4 text-center">
      <Icon className="w-5 h-5 mx-auto mb-2" style={{ color: "#39E6FF" }} />
      <div className="font-display text-xl font-bold text-primary mb-0.5">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-text-tertiary">{label}</div>
    </div>
  );
}

export function LandingPage() {
  const { tokenCount } = useLaunchpadConfig();
  const count = Number(tokenCount) > 0 ? Number(tokenCount).toLocaleString() : "—";

  return (
    <div className="cosmic-stage">

      {/* ══════════════════════════════════════════════════════
          HERO — full-bleed cinematic image
      ══════════════════════════════════════════════════════ */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: "80vh" }}>
        {/* Full-bleed hero image */}
        <img
          src={HERO}
          alt="JASMY DJNN"
          className="absolute inset-0 w-full h-full object-cover object-center select-none"
          draggable={false}
        />

        {/* Dark gradient overlay for text legibility — bottom only, keeps top image vivid */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to bottom, rgba(6,9,16,0.15) 0%, rgba(6,9,16,0.55) 60%, rgba(6,9,16,0.97) 100%)",
          }}
        />

        {/* Content overlay */}
        <div className="relative z-10 flex flex-col items-center justify-end h-full pb-16 px-4 sm:px-6 text-center" style={{ minHeight: "80vh" }}>
          {/* Live badge */}
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium mb-6 border"
            style={{ background: "rgba(57,230,255,0.1)", borderColor: "rgba(57,230,255,0.3)", color: "#7FF0FB" }}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            Live on JasmyChain 680
          </div>

          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black leading-none mb-4 text-white"
              style={{ textShadow: "0 0 60px rgba(57,230,255,0.4), 0 2px 8px rgba(0,0,0,0.8)" }}>
            JASMY SWAP
          </h1>

          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-8"
             style={{ textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}>
            JasmyChain's premier decentralized exchange — V4 Infinity pools, cross-chain bridge,
            and DJNN reward distribution. Non-custodial. Secured by JASMY.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Link to="/swap" className="btn-gold inline-flex items-center gap-2 text-base px-8 py-3.5">
              <ArrowLeftRight className="w-5 h-5" /> Start Trading
            </Link>
            <Link
              to="/rewards"
              className="inline-flex items-center gap-2 text-base px-8 py-3.5 rounded-xl font-semibold border transition-all"
              style={{ borderColor: "rgba(57,230,255,0.4)", color: "#9FFCE8", background: "rgba(57,230,255,0.06)" }}
            >
              <Coins className="w-5 h-5" /> Claim Rewards
            </Link>
          </div>

          {/* Nav chips */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { to: "/swap",      icon: ArrowLeftRight, label: "Swap"      },
              { to: "/liquidity", icon: Droplets,       label: "Liquidity" },
              { to: "/launchpad", icon: Rocket,         label: "Launchpad" },
              { to: "/rewards",   icon: Coins,          label: "Rewards"   },
              { to: "/bridge",    icon: Link2,          label: "Bridge"    },
              { to: "/farms",     icon: Sparkles,       label: "Farms"     },
            ].map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all hover:border-primary/60"
                style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)" }}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          STATS STRIP
      ══════════════════════════════════════════════════════ */}
      <section className="relative z-[1] max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatPill label="Chain ID"      value="680"        icon={Zap}       />
          <StatPill label="Tokens Listed" value={count}      icon={TrendingUp} />
          <StatPill label="Pool Types"    value="V2 · CL · Bin" icon={Droplets}  />
          <StatPill label="Gas Fees"      value="Ultra Low"  icon={Shield}    />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          DJNN REWARDS — full-bleed cinematic image
      ══════════════════════════════════════════════════════ */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: "60vh" }}>
        {/* Full-bleed genie image */}
        <img
          src={GENIE}
          alt="DJNN — Cyber Djinn rewards"
          className="absolute inset-0 w-full h-full object-cover object-center select-none"
          draggable={false}
        />

        {/* Overlay — left side vivid, right side dark for text */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to right, rgba(6,9,16,0.1) 0%, rgba(6,9,16,0.3) 40%, rgba(6,9,16,0.92) 75%, rgba(6,9,16,0.97) 100%)",
          }}
        />
        {/* Bottom fade to page */}
        <div
          className="absolute bottom-0 left-0 right-0 h-32"
          style={{ background: "linear-gradient(to bottom, transparent, rgba(6,9,16,1))" }}
        />

        {/* Content — right-aligned on desktop */}
        <div className="relative z-10 flex items-center h-full px-4 sm:px-6 py-16" style={{ minHeight: "60vh" }}>
          <div className="ml-auto max-w-lg w-full">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest mb-3"
                 style={{ color: "#E8B65A" }}>
              <Sparkles className="w-3.5 h-3.5" /> Earn Yield
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-black text-white mb-4 leading-tight"
                style={{ textShadow: "0 0 40px rgba(232,182,90,0.3), 0 2px 8px rgba(0,0,0,0.9)" }}>
              Hold DJNN.<br />Earn Automatically.
            </h2>
            <p className="text-white/70 text-base mb-4 leading-relaxed max-w-md">
              DJNN distributes native JASMY gas, pJASMY, pDAI, pGYEN and pWBTC directly to
              every holder on every transfer. No staking. No claiming. Just hold.
            </p>
            <p className="text-white/50 text-sm mb-8 max-w-md">
              Five reward slots, auto-distributed via embedded distributor contracts. 6% tax
              split across all reward channels — the token works for you 24/7.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/rewards" className="btn-gold inline-flex items-center gap-2 px-6 py-3">
                <Coins className="w-4 h-4" /> View Rewards
              </Link>
              <Link
                to="/swap"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border transition-all"
                style={{ borderColor: "rgba(232,182,90,0.4)", color: "#E8B65A", background: "rgba(232,182,90,0.06)" }}
              >
                <ArrowLeftRight className="w-4 h-4" /> Buy DJNN
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FEATURES GRID
      ══════════════════════════════════════════════════════ */}
      <section className="relative z-[1] max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-text-primary mb-3">Everything You Need</h2>
          <p className="text-text-secondary">A complete DeFi stack, native to JasmyChain</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard title="Token Swap"          icon={ArrowLeftRight} href="/swap"      accent="#39E6FF"
            description="Smart routing across V2 AMM and V4 Infinity pools. Low fees, FoT token support, instant execution." />
          <FeatureCard title="V4 Infinity Pools"   icon={Droplets}       href="/liquidity" accent="#9FFCE8"
            description="Concentrated and Bin pools on a singleton Vault. Set price ranges, earn up to 4000× more per dollar." />
          <FeatureCard title="DJNN Rewards"        icon={Coins}          href="/rewards"   accent="#E8B65A"
            description="Hold DJNN to earn 5 reward tokens auto-distributed on every transfer. Zero claiming, zero staking." />
          <FeatureCard title="Token Launchpad"     icon={Rocket}         href="/launchpad" accent="#C724B1"
            description="Fair-launch tokens with bonding curves. Liquidity graduates straight to the DEX at full fill." />
          <FeatureCard title="Cross-Chain Bridge"  icon={Link2}          href="/bridge"    accent="#39E6FF"
            description="Move assets between PulseChain and JasmyChain via BridgeIT with full pToken support." />
          <FeatureCard title="Vote-Escrow"         icon={Lock}           href="/ve"        accent="#9FFCE8"
            description="Lock DJNN for veDJNN — boosted reward multipliers and governance over the protocol." />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CTA — emblem + button
      ══════════════════════════════════════════════════════ */}
      <section className="relative z-[1] max-w-3xl mx-auto px-4 sm:px-6 pb-24 text-center">
        <div className="glass-aqua p-8 sm:p-12">
          <img
            src={EMBLEM}
            alt="Jasmy"
            className="w-20 h-20 rounded-full mx-auto mb-6 object-cover"
            style={{ boxShadow: "0 0 40px rgba(199,36,177,0.4)" }}
          />
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-text-primary mb-3">
            Built for Speed. Secured by JASMY.
          </h2>
          <p className="text-text-secondary mb-8 max-w-lg mx-auto">
            Connect your wallet and experience Jasmy Swap on JasmyChain 680.
          </p>
          <Link to="/swap" className="btn-gold inline-flex items-center gap-2 text-base px-8 py-3.5">
            Launch App <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

    </div>
  );
}
